import React, { useState, useEffect } from 'react';
import { ProjectRequestData, ProjectRequestErrors } from '../../types';
import { autoSaveFormData, loadAutoSavedFormData, clearAutoSavedFormData } from '../../utils/storage';

interface ProjectRequestFormProps {
  onSubmit: (data: ProjectRequestData) => void;
  onClear: () => void;
}

const ProjectRequestForm: React.FC<ProjectRequestFormProps> = ({ onSubmit, onClear }) => {
  const [formData, setFormData] = useState<ProjectRequestData>({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    projectName: '',
    projectDescription: '',
    projectType: '',
    technologyPreferences: '',
    estimatedBudgetRange: '',
    timeline: '',
    preferredStartDate: '',
    additionalRequirements: ''
  });

  const [errors, setErrors] = useState<ProjectRequestErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customProjectType, setCustomProjectType] = useState('');

  // Load auto-saved data on component mount
  useEffect(() => {
    const savedData = loadAutoSavedFormData('project');
    if (savedData) {
      setFormData(savedData);
    }
  }, []);

  // Auto-save form data every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      autoSaveFormData('project', formData);
    }, 30000);

    return () => clearInterval(interval);
  }, [formData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle project type selection
    if (name === 'projectType') {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (value === 'Other') {
        setCustomProjectType('');
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name as keyof ProjectRequestErrors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ProjectRequestErrors = {};

    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.projectName.trim()) newErrors.projectName = 'Project name is required';
    if (!formData.projectType) newErrors.projectType = 'Project type is required';
    if (!formData.timeline) newErrors.timeline = 'Timeline is required';
    if (!formData.preferredStartDate) newErrors.preferredStartDate = 'Preferred start date is required';

    // Email validation (if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      clearAutoSavedFormData('project');
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        projectName: '',
        projectDescription: '',
        projectType: '',
        technologyPreferences: '',
        estimatedBudgetRange: '',
        timeline: '',
        preferredStartDate: '',
        additionalRequirements: ''
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear the form? This action cannot be undone.')) {
      clearAutoSavedFormData('project');
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        projectName: '',
        projectDescription: '',
        projectType: '',
        technologyPreferences: '',
        estimatedBudgetRange: '',
        timeline: '',
        preferredStartDate: '',
        additionalRequirements: ''
      });
      setErrors({});
      onClear();
    }
  };

  const projectTypes = [
    'Web Application',
    'Mobile Application',
    'Desktop Application',
    'API Development',
    'Full Stack',
    'SAP Implementation',
    'SAP Customization',
    'SAP Integration',
    'SAP Migration',
    'SAP Support & Maintenance',
    'SAP Fiori Development',
    'SAP ABAP Development',
    'SAP BW/BI',
    'SAP HANA',
    'SAP SuccessFactors',
    'SAP Ariba',
    'SAP Concur',
    'Other'
  ];

  const timelineOptions = [
    'Urgent (1-2 months)',
    'Standard (3-6 months)',
    'Flexible (6+ months)'
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Company Information */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-ink-900 mb-4">Company Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-ink-900 mb-2">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                errors.companyName ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
              }`}
              placeholder="Enter your company name"
            />
            {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
          </div>
          <div>
            <label htmlFor="contactPerson" className="block text-sm font-medium text-ink-900 mb-2">
              Contact Person
            </label>
            <input
              type="text"
              id="contactPerson"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
              placeholder="Your name"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink-900 mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
              }`}
              placeholder="your.email@company.com"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-ink-900 mb-2">
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
              placeholder="+84 123 456 789"
            />
          </div>
        </div>
      </div>

      {/* Project Information */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-ink-900 mb-4">Project Information</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="projectName" className="block text-sm font-medium text-ink-900 mb-2">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="projectName"
              name="projectName"
              value={formData.projectName}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                errors.projectName ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
              }`}
              placeholder="Enter project name"
            />
            {errors.projectName && <p className="text-red-500 text-sm mt-1">{errors.projectName}</p>}
          </div>
          <div>
            <label htmlFor="projectDescription" className="block text-sm font-medium text-ink-900 mb-2">
              Project Description
            </label>
            <textarea
              id="projectDescription"
              name="projectDescription"
              rows={4}
              value={formData.projectDescription}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200 resize-vertical"
              placeholder="Describe your project requirements, goals, and expectations"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="projectType" className="block text-sm font-medium text-ink-900 mb-2">
                Project Type <span className="text-red-500">*</span>
              </label>
              <select
                id="projectType"
                name="projectType"
                value={formData.projectType}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                  errors.projectType ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
                }`}
              >
                <option value="">Select project type</option>
                {projectTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {formData.projectType === 'Other' && (
                <input
                  type="text"
                  value={customProjectType}
                  onChange={(e) => {
                    setCustomProjectType(e.target.value);
                    setFormData(prev => ({ ...prev, projectType: e.target.value }));
                  }}
                  placeholder="Please specify project type"
                  className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200 mt-2"
                />
              )}
              {errors.projectType && <p className="text-red-500 text-sm mt-1">{errors.projectType}</p>}
            </div>
            <div>
              <label htmlFor="timeline" className="block text-sm font-medium text-ink-900 mb-2">
                Timeline <span className="text-red-500">*</span>
              </label>
              <select
                id="timeline"
                name="timeline"
                value={formData.timeline}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                  errors.timeline ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
                }`}
              >
                <option value="">Select timeline</option>
                {timelineOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {errors.timeline && <p className="text-red-500 text-sm mt-1">{errors.timeline}</p>}
            </div>
          </div>
          <div>
            <label htmlFor="technologyPreferences" className="block text-sm font-medium text-ink-900 mb-2">
              Technology Preferences
            </label>
            <textarea
              id="technologyPreferences"
              name="technologyPreferences"
              rows={3}
              value={formData.technologyPreferences}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200 resize-vertical"
              placeholder="e.g., React, Node.js, PostgreSQL, AWS"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="estimatedBudgetRange" className="block text-sm font-medium text-ink-900 mb-2">
                Estimated Budget Range
              </label>
              <input
                type="text"
                id="estimatedBudgetRange"
                name="estimatedBudgetRange"
                value={formData.estimatedBudgetRange}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
                placeholder="e.g., $10,000 - $50,000"
              />
            </div>
            <div>
              <label htmlFor="preferredStartDate" className="block text-sm font-medium text-ink-900 mb-2">
                Preferred Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="preferredStartDate"
                name="preferredStartDate"
                value={formData.preferredStartDate}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border-2 rounded-lg text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 date-picker-enhanced ${
                  errors.preferredStartDate ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
                }`}
                style={{ 
                  backgroundColor: '#1f2937',
                  color: '#e5e7eb'
                }}
              />
              {errors.preferredStartDate && <p className="text-red-500 text-sm mt-1">{errors.preferredStartDate}</p>}
            </div>
          </div>
          <div>
            <label htmlFor="additionalRequirements" className="block text-sm font-medium text-ink-900 mb-2">
              Additional Requirements
            </label>
            <textarea
              id="additionalRequirements"
              name="additionalRequirements"
              rows={4}
              value={formData.additionalRequirements}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200 resize-vertical"
              placeholder="Any additional requirements, constraints, or special considerations"
            />
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-end">
        <button
          type="button"
          onClick={handleClear}
          className="btn-secondary"
        >
          Clear Form
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Project Request'}
        </button>
      </div>
    </form>
  );
};

export default ProjectRequestForm;
